import { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { CompareSeries } from "../api/types";
import { echarts, PALETTE, baseAxis, baseTooltip } from "../charts/echarts";

interface Bar {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

type Preset = "1d" | "5d" | "1m" | "3m" | "6m" | "1y" | "all";
type Band = "none" | "bollinger" | "donchian";

const PRESETS: { key: Preset; label: string; interval: "1h" | "1d"; days: number }[] = [
  { key: "1d",  label: "1D",  interval: "1h", days: 2 },
  { key: "5d",  label: "5D",  interval: "1h", days: 5 },
  { key: "1m",  label: "1M",  interval: "1d", days: 31 },
  { key: "3m",  label: "3M",  interval: "1d", days: 92 },
  { key: "6m",  label: "6M",  interval: "1d", days: 183 },
  { key: "1y",  label: "1Y",  interval: "1d", days: 365 },
  { key: "all", label: "All", interval: "1d", days: 1825 },
];

const BANDS: { key: Band; label: string; title: string }[] = [
  { key: "none",      label: "None",  title: "Hide bands" },
  { key: "bollinger", label: "BB",    title: "Bollinger Bands — 20-period SMA ± 2σ" },
  { key: "donchian",  label: "DC",    title: "Donchian Channels — 20-period highest high / lowest low" },
];

const BAND_PERIOD = 20;
const BB_MULT = 2;

function bollinger(closes: number[], period = BAND_PERIOD, mult = BB_MULT) {
  const upper: (number | null)[] = [];
  const middle: (number | null)[] = [];
  const lower: (number | null)[] = [];
  for (let i = 0; i < closes.length; i++) {
    if (i < period - 1) {
      upper.push(null); middle.push(null); lower.push(null);
      continue;
    }
    let sum = 0;
    for (let k = i - period + 1; k <= i; k++) sum += closes[k];
    const mean = sum / period;
    let varSum = 0;
    for (let k = i - period + 1; k <= i; k++) varSum += (closes[k] - mean) ** 2;
    const sd = Math.sqrt(varSum / period);
    middle.push(mean);
    upper.push(mean + mult * sd);
    lower.push(mean - mult * sd);
  }
  return { upper, middle, lower };
}

function donchian(highs: number[], lows: number[], period = BAND_PERIOD) {
  const upper: (number | null)[] = [];
  const lower: (number | null)[] = [];
  for (let i = 0; i < highs.length; i++) {
    if (i < period - 1) {
      upper.push(null); lower.push(null);
      continue;
    }
    let hi = -Infinity, lo = Infinity;
    for (let k = i - period + 1; k <= i; k++) {
      if (highs[k] > hi) hi = highs[k];
      if (lows[k] < lo) lo = lows[k];
    }
    upper.push(hi);
    lower.push(lo);
  }
  return { upper, lower };
}

function formatTs(ts: string, intraday: boolean): string {
  const d = new Date(ts);
  if (intraday) {
    return `${d.toLocaleDateString(undefined, { month: "short", day: "2-digit" })} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
  }
  return d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" });
}

export default function PriceChart({
  yahooSymbol,
  compare,
}: {
  yahooSymbol: string;
  compare?: CompareSeries[];
}) {
  const [preset, setPreset] = useState<Preset>("3m");
  const [band, setBand] = useState<Band>("bollinger");
  const cfg = PRESETS.find((p) => p.key === preset)!;
  const compareMode = !!compare && compare.length === 2;

  const bars = useQuery<Bar[]>({
    queryKey: ["bars", yahooSymbol, cfg.interval, cfg.days],
    queryFn: async () =>
      (await api.get<Bar[]>(`/symbols/${encodeURIComponent(yahooSymbol)}/bars`, {
        params: { interval: cfg.interval, days: cfg.days },
      })).data,
    enabled: !!yahooSymbol,
    staleTime: 60_000,
  });

  const compareOption = useMemo(() => {
    if (!compareMode) return null;
    const [a, b] = compare!;
    return {
      backgroundColor: "transparent",
      animation: false,
      tooltip: {
        ...baseTooltip,
        valueFormatter: (v: unknown) =>
          typeof v === "number" ? `${v >= 0 ? "+" : ""}${v.toFixed(2)}%` : String(v),
        axisPointer: { type: "cross", lineStyle: { color: PALETTE.muted } },
      },
      legend: { textStyle: { color: PALETTE.text, fontSize: 11 }, top: 0, right: 12 },
      grid: { left: 50, right: 16, top: 28, bottom: 28 },
      xAxis: { ...baseAxis, type: "time" },
      yAxis: {
        ...baseAxis,
        type: "value",
        scale: true,
        splitNumber: 4,
        axisLabel: { ...baseAxis.axisLabel, formatter: "{value}%" },
      },
      series: [
        {
          name: a.yahoo_symbol,
          type: "line",
          showSymbol: false,
          smooth: false,
          lineStyle: { color: PALETTE.accent, width: 1.5 },
          itemStyle: { color: PALETTE.accent },
          data: a.series.map((p) => [p.ts, p.indexed]),
        },
        {
          name: b.yahoo_symbol,
          type: "line",
          showSymbol: false,
          smooth: false,
          lineStyle: { color: "#fbbf24", width: 1.5 },
          itemStyle: { color: "#fbbf24" },
          data: b.series.map((p) => [p.ts, p.indexed]),
        },
      ],
    } satisfies echarts.EChartsCoreOption;
  }, [compareMode, compare]);

  const option = useMemo(() => {
    const data = bars.data ?? [];
    const intraday = cfg.interval === "1h";
    const categories = data.map((b) => formatTs(b.ts, intraday));
    const ohlc = data.map((b) => [b.open, b.close, b.low, b.high]);
    const volumes = data.map((b, i) => ({
      value: b.volume,
      itemStyle: { color: b.close >= b.open ? PALETTE.up : PALETTE.down, opacity: 0.5 },
      xAxisIndex: 0,
      seriesIndex: i,
    }));

    const bandSeries: unknown[] = [];
    if (band === "bollinger" && data.length >= BAND_PERIOD) {
      const closes = data.map((b) => b.close);
      const { upper, middle, lower } = bollinger(closes);
      const lineCommon = { type: "line", showSymbol: false, smooth: true, xAxisIndex: 0, yAxisIndex: 0, z: 1 };
      bandSeries.push(
        { ...lineCommon, name: "BB upper", data: upper, lineStyle: { color: PALETTE.accent, width: 1, type: "dashed", opacity: 0.7 }, areaStyle: { color: "rgba(56,189,248,0.06)", origin: "start" } },
        { ...lineCommon, name: "BB lower", data: lower, lineStyle: { color: PALETTE.accent, width: 1, type: "dashed", opacity: 0.7 } },
        { ...lineCommon, name: "BB SMA20", data: middle, lineStyle: { color: PALETTE.muted, width: 1, type: "dotted", opacity: 0.8 } },
      );
    } else if (band === "donchian" && data.length >= BAND_PERIOD) {
      const highs = data.map((b) => b.high);
      const lows = data.map((b) => b.low);
      const { upper, lower } = donchian(highs, lows);
      const lineCommon = { type: "line", showSymbol: false, smooth: false, step: "end", xAxisIndex: 0, yAxisIndex: 0, z: 1 };
      bandSeries.push(
        { ...lineCommon, name: "DC upper", data: upper, lineStyle: { color: PALETTE.bull, width: 1, type: "dashed", opacity: 0.6 } },
        { ...lineCommon, name: "DC lower", data: lower, lineStyle: { color: PALETTE.bear, width: 1, type: "dashed", opacity: 0.6 } },
      );
    }

    return {
      backgroundColor: "transparent",
      animation: false,
      tooltip: {
        ...baseTooltip,
        axisPointer: { type: "cross", lineStyle: { color: PALETTE.muted } },
      },
      grid: [
        { left: 50, right: 16, top: 12, height: "62%" },
        { left: 50, right: 16, top: "76%", height: "14%" },
      ],
      xAxis: [
        { ...baseAxis, type: "category", data: categories, boundaryGap: true, axisLabel: { ...baseAxis.axisLabel, hideOverlap: true } },
        { ...baseAxis, type: "category", gridIndex: 1, data: categories, axisLabel: { show: false } },
      ],
      yAxis: [
        { ...baseAxis, scale: true, splitNumber: 4 },
        {
          ...baseAxis,
          gridIndex: 1,
          scale: true,
          splitNumber: 2,
          axisLabel: {
            ...baseAxis.axisLabel,
            fontSize: 9,
            formatter: (v: number) => {
              const abs = Math.abs(v);
              if (abs >= 1e9) return `${(v / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}B`;
              if (abs >= 1e6) return `${(v / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
              if (abs >= 1e3) return `${(v / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
              return String(v);
            },
          },
        },
      ],
      dataZoom: [
        { type: "inside", xAxisIndex: [0, 1], start: 0, end: 100 },
        { type: "slider", xAxisIndex: [0, 1], height: 16, bottom: 4, borderColor: PALETTE.grid, fillerColor: "rgba(56,189,248,0.12)", handleStyle: { color: PALETTE.accent }, textStyle: { color: PALETTE.muted, fontSize: 9 } },
      ],
      series: [
        ...bandSeries,
        {
          name: "Price",
          type: "candlestick",
          data: ohlc,
          z: 2,
          itemStyle: {
            color: PALETTE.up,
            color0: PALETTE.down,
            borderColor: PALETTE.up,
            borderColor0: PALETTE.down,
          },
        },
        {
          name: "Volume",
          type: "bar",
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: volumes,
          large: true,
        },
      ],
    } satisfies echarts.EChartsCoreOption;
  }, [bars.data, cfg.interval, band]);

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-3 py-2">
        <div className="flex items-baseline gap-2">
          <div className="text-sm font-medium text-slate-300">{compareMode ? "Compare" : "Price"}</div>
          {compareMode && (
            <span className="text-[11px] text-slate-500">indexed · 90d</span>
          )}
        </div>
        {!compareMode && (
          <div className="flex flex-wrap items-center gap-2">
            {bars.isLoading && <div className="text-[11px] text-slate-500">loading…</div>}
            <div className="flex gap-0.5 rounded-md border border-slate-700 bg-slate-950 p-0.5 text-[10px]" title="Overlay bands">
              {BANDS.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setBand(b.key)}
                  title={b.title}
                  className={`rounded px-1.5 py-0.5 ${
                    band === b.key ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            <div className="flex gap-0.5 rounded-md border border-slate-700 bg-slate-950 p-0.5 text-[10px]">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.key)}
                  className={`rounded px-1.5 py-0.5 ${
                    preset === p.key ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {compareMode && compareOption ? (
        <ReactECharts option={compareOption} style={{ height: 320, width: "100%" }} notMerge={true} lazyUpdate={true} echarts={echarts} />
      ) : bars.data && bars.data.length === 0 ? (
        <div className="px-3 py-8 text-center text-[11px] text-slate-500">
          No bars yet for this timeframe.
        </div>
      ) : (
        <ReactECharts option={option} style={{ height: 320, width: "100%" }} notMerge={true} lazyUpdate={true} echarts={echarts} />
      )}
    </section>
  );
}
